import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import GoogleAnalyticsService from "../../api/googleAnalytics";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState(
    "Google Analytics verbinding wordt verwerkt...",
  );

  useEffect(() => {
    const code = searchParams.get("code");
    const error = searchParams.get("error");

    if (error) {
      setStatus("error");
      setMessage(
        error === "access_denied"
          ? "U heeft de toegang geweigerd. Probeer het opnieuw."
          : `Er is een fout opgetreden: ${error}`,
      );
      setTimeout(() => navigate("/dashboard/statistics"), 3000);
      return;
    }

    if (!code) {
      setStatus("error");
      setMessage("Geen autorisatiecode ontvangen. Probeer het opnieuw.");
      setTimeout(() => navigate("/dashboard/statistics"), 3000);
      return;
    }

    handleCallback(code);
  }, [searchParams]);

  const handleCallback = async (code) => {
    try {
      const data = await GoogleAnalyticsService.handleOAuthCallback(code);

      if (data && data.access_token) {
        localStorage.setItem("google_analytics_token", data.access_token);
        if (data.refresh_token) {
          localStorage.setItem(
            "google_analytics_refresh_token",
            data.refresh_token,
          );
        }

        setStatus("success");
        setMessage("Google Analytics is succesvol verbonden!");
        setTimeout(() => navigate("/dashboard/statistics"), 2000);
      } else {
        throw new Error("No access token received");
      }
    } catch (error) {
      console.error("Google OAuth callback error:", error);
      setStatus("error");
      setMessage(
        "Er is een fout opgetreden bij het verbinden. Probeer het opnieuw.",
      );
      setTimeout(() => navigate("/dashboard/statistics"), 3000);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#0a0a0a]" : "bg-gray-50"}`}
    >
      <div
        className={`rounded-xl p-8 text-center max-w-sm w-full mx-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        {status === "loading" && (
          <div
            className={`w-10 h-10 rounded-full border-2 animate-spin mx-auto mb-4 ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
          />
        )}
        {status === "success" && (
          <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-5 w-5 text-emerald-500" />
          </div>
        )}
        {status === "error" && (
          <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-5 w-5 text-red-500" />
          </div>
        )}

        <p
          className={`text-[14px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
        >
          {message}
        </p>
        <p
          className={`text-[12px] mt-2 ${isDark ? "text-gray-600" : "text-gray-500"}`}
        >
          U wordt automatisch doorgestuurd...
        </p>
      </div>
    </div>
  );
};

export default GoogleCallback;
